import React, {useState, useEffect, useContext} from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import {Button, Icon, Text} from '@albert-io/atomic';

import {AssignmentContext, isDueDatePast} from '../assignmentPracticeView.utils';

import {renderSecondsAsTimeString, isTimerUndismissable} from './timeManager.utils';
import './time-manager.scss';

export const Timer = ({secondsLeft, totalSeconds}) => {
  const halfwaySeconds = Math.floor(totalSeconds / 2);
  const isUndismissable = isTimerUndismissable(secondsLeft);
  const isWarnMode =
    isUndismissable || (halfwaySeconds > secondsLeft && halfwaySeconds - secondsLeft <= 5);
  const [isCollapsed, setIsCollapsed] = useState(false);
  return (
    <Button
      size='s'
      color='secondary'
      variant='text'
      role='timer'
      aria-label='Toggle timer visibility'
      disabled={isWarnMode}
      className={classnames('assignment-timer u-mar-r_1', {
        'assignment-timer--warn': isWarnMode,
        'assignment-timer--undismissable': isUndismissable
      })}
      onClick={isWarnMode ? null : () => setIsCollapsed(!isCollapsed)}
    >
      <Icon icon='clock' iconStyle='regular' />
      {isCollapsed && !isWarnMode ? null : (
        <Text bold size='s' className='u-mar-l_1'>
          {' '}
          {renderSecondsAsTimeString(secondsLeft)}
        </Text>
      )}
    </Button>
  );
};

Timer.propTypes = {
  secondsLeft: PropTypes.number.isRequired,
  totalSeconds: PropTypes.number.isRequired
};

const TimeManager = ({onOutOfTime, secondsLeft, ...rest}) => {
  const {assignment, classroomId, studentId} = useContext(AssignmentContext);
  /**
   * We're out of time if a timed assignment's time is up
   * or if the due date has passed and the assignment doesn't
   * allow late submissions.
   */
  const isOutOfTime =
    (secondsLeft !== null && secondsLeft <= 0) ||
    (isDueDatePast({assignment, classroomId, studentId}) &&
      !assignment.getIsAllowLateSubmissionsFromSettings({classroomId, studentId}));

  useEffect(() => {
    if (isOutOfTime) {
      onOutOfTime();
    }
  }, [isOutOfTime, onOutOfTime]);

  return (
    secondsLeft !== null && (
      <Timer
        secondsLeft={secondsLeft <= 0 ? 0 : secondsLeft}
        totalSeconds={assignment.getTimeLimit() * 60}
        {...rest}
      />
    )
  );
};

TimeManager.propTypes = {
  isGraded: PropTypes.bool,
  onOutOfTime: PropTypes.func.isRequired,
  secondsLeft: PropTypes.number
};

export default TimeManager;
